'use client';

import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';
import { cn } from '@/lib/utils';

export type MultiSelectItem = {
  value: string;
  label: string;
  count?: number;
};

export type MultiSelectGroup = {
  label: string;
  items: MultiSelectItem[];
};

interface MultiSelectPopoverProps {
  label: string;
  placeholder?: string;
  items?: MultiSelectItem[];
  groups?: MultiSelectGroup[];
  selected: string[];
  onChange: (next: string[]) => void;
  ariaLabel?: string;
}

function renderItem(
  item: MultiSelectItem,
  selected: string[],
  toggleItem: (v: string) => void
) {
  const isSelected = selected.includes(item.value);
  return (
    <CommandItem
      key={item.value}
      value={item.value}
      onSelect={() => toggleItem(item.value)}
      className="flex items-center justify-between"
    >
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'flex h-4 w-4 items-center justify-center rounded-sm border',
            isSelected
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-muted-foreground/30'
          )}
        >
          {isSelected && <IconCheck className="size-3" />}
        </div>
        <span className="truncate">{item.label}</span>
      </div>
      {item.count !== undefined && (
        <span className="text-xs text-muted-foreground">{item.count}</span>
      )}
    </CommandItem>
  );
}

export function MultiSelectPopover({
  label,
  placeholder = 'Search...',
  items,
  groups,
  selected,
  onChange,
  ariaLabel,
}: MultiSelectPopoverProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const isGrouped = Boolean(groups && groups.length > 0);

  const allItems: MultiSelectItem[] = useMemo(() => {
    if (isGrouped && groups) return groups.flatMap((g) => g.items);
    return items ?? [];
  }, [isGrouped, groups, items]);

  const filteredGroups: MultiSelectGroup[] = useMemo(() => {
    if (!isGrouped || !groups) return [];
    if (!search) return groups;
    const query = search.toLowerCase();
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => item.label.toLowerCase().includes(query)),
      }))
      .filter((g) => g.items.length > 0);
  }, [isGrouped, groups, search]);

  const filteredItems: MultiSelectItem[] = useMemo(() => {
    if (isGrouped) return [];
    if (!search) return allItems;
    const query = search.toLowerCase();
    return allItems.filter((item) => item.label.toLowerCase().includes(query));
  }, [isGrouped, allItems, search]);

  const toggleItem = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleSelectAll = () => {
    onChange(allItems.map((item) => item.value));
  };

  const handleClearSelection = () => {
    onChange([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1"
          aria-label={ariaLabel || label}
        >
          {label}
          {selected.length > 0 && (
            <span className="ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/20 px-1.5 text-xs font-medium text-primary">
              {selected.length}
            </span>
          )}
          <IconChevronDown className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={search}
            onValueChange={setSearch}
          />
          <div className="flex items-center justify-between border-b px-2 py-1.5">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={handleClearSelection}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          </div>
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            {isGrouped ? (
              filteredGroups.map((group) => (
                <CommandGroup key={group.label} heading={group.label}>
                  {group.items.map((item) => renderItem(item, selected, toggleItem))}
                </CommandGroup>
              ))
            ) : (
              <CommandGroup>
                {filteredItems.map((item) => renderItem(item, selected, toggleItem))}
              </CommandGroup>
            )}
          </CommandList>
          {selected.length > 0 && (
            <div className="border-t px-2 py-1.5 text-xs text-muted-foreground">
              {selected.length} selected
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
